import { FC } from 'react';
import {
  CoverageGap,
  CoverageSegment,
  catchUpEstimate,
  coverageSegments,
  failedRunMarks,
  formatSpan,
} from './coverageGaps';
import { formatDuration } from './assessRecovery';

type CoverageTimelineProps = {
  rangeStart: number;
  rangeEnd: number;
  gaps: CoverageGap[];
  runs: Array<{ startDate?: string; status?: string }>;
  selectedId: string | null;
  onSelect: (gap: CoverageGap) => void;
  canFetch: boolean;
  pollIntervalSeconds: number | null;
};

function axisLabel(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CoverageTimeline: FC<CoverageTimelineProps> = ({
  rangeStart,
  rangeEnd,
  gaps,
  runs,
  selectedId,
  onSelect,
  canFetch,
  pollIntervalSeconds,
}) => {
  const segments: CoverageSegment[] = coverageSegments(gaps, rangeStart, rangeEnd);
  const failures = failedRunMarks(runs, rangeStart, rangeEnd);
  const missedMs = gaps.reduce((sum, gap) => sum + gap.durationMs, 0);

  return (
    <div className="studio-coverage">
      <div className="studio-coverage__axis">
        <span>{axisLabel(rangeStart)}</span>
        <span>
          {gaps.length
            ? `${gaps.length} missed ${gaps.length === 1 ? 'span' : 'spans'} · ${formatSpan(missedMs)}`
            : 'No missed span in this range'}
        </span>
        <span>{axisLabel(rangeEnd)}</span>
      </div>
      <div
        className="studio-coverage__track"
        role="img"
        aria-label={
          gaps.length
            ? `Coverage from ${axisLabel(rangeStart)} to ${axisLabel(rangeEnd)}. ${gaps.length} missed ${gaps.length === 1 ? 'span' : 'spans'}.`
            : `Coverage from ${axisLabel(rangeStart)} to ${axisLabel(rangeEnd)}. No missed span.`
        }
      >
        {segments.map((segment) => (
          segment.kind === 'missed' && segment.gap ? (
            <button
              key={segment.gap.id}
              type="button"
              className={`studio-coverage__gap${selectedId === segment.gap.id ? ' is-on' : ''}`}
              style={{ left: `${segment.leftPct}%`, width: `${Math.max(segment.widthPct, 0.6)}%` }}
              onClick={() => onSelect(segment.gap!)}
              title={`Missed ${formatSpan(segment.gap.durationMs)}`}
            />
          ) : (
            <span
              key={`ok-${segment.from}`}
              className="studio-coverage__ok"
              style={{ left: `${segment.leftPct}%`, width: `${Math.max(segment.widthPct, 0.2)}%` }}
            />
          )
        ))}
        {failures.map((mark) => (
          <span
            key={mark.at}
            className="studio-coverage__fail"
            style={{ left: `${mark.leftPct}%` }}
            title="Failed run"
          />
        ))}
      </div>
      <div className="studio-coverage__legend">
        <span className="is-ok">Covered</span>
        <span className="is-miss">Missed</span>
        <span className="is-fail">Failed run</span>
      </div>
      {gaps.length ? (
        <ul className="studio-coverage__list">
          {gaps.map((gap) => {
            const estimate = catchUpEstimate(gap.durationMs, pollIntervalSeconds);
            return (
              <li key={gap.id}>
                <button
                  type="button"
                  className={selectedId === gap.id ? 'is-on' : ''}
                  onClick={() => onSelect(gap)}
                >
                  <strong>Missed {formatSpan(gap.durationMs)}</strong>
                  <span>
                    {axisLabel(Date.parse(gap.from))} → {axisLabel(Date.parse(gap.to))}
                    {canFetch
                      ? ` · about ${estimate.polls} poll${estimate.polls === 1 ? '' : 's'} (${formatDuration(estimate.wallMs)})`
                      : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default CoverageTimeline;
